/**
 * Normalizes AI edge function errors from the two transports used in this
 * codebase (raw fetch for SSE streaming, and supabase.functions.invoke for
 * everything else) into a thrown-ready Error that carries `.code`, `.status`,
 * and the server's message.
 *
 * Why: AI enforcement responses (403 with { success: false, error: { code,
 * message } }) were being collapsed to generic "Request failed" / "Stream
 * failed: 403" Errors, which downstream error classification could no longer
 * distinguish from true network failures.
 */

const KNOWN_AI_ENFORCEMENT_CODES = new Set([
  'AI_NOT_ENABLED',
  'AI_LICENSE_BLOCKED',
  'AI_USAGE_CAP_REACHED',
]);

export function isAiEnforcementCode(code) {
  return typeof code === 'string' && KNOWN_AI_ENFORCEMENT_CODES.has(code);
}

function attach(err, code, status) {
  if (code) err.code = code;
  if (typeof status === 'number') err.status = status;
  return err;
}

/**
 * Builds an Error from a raw fetch Response. Reads body via clone() so the
 * caller can still consume the response afterward if needed.
 */
export async function errorFromAiFetchResponse(response, fallbackMessage = 'Request failed') {
  let code = null;
  let message = fallbackMessage;
  try {
    const body = await response.clone().json();
    if (body?.error?.code) code = body.error.code;
    if (body?.error?.message) message = body.error.message;
    else if (typeof body?.text === 'string' && body.text) message = body.text;
  } catch (_) {
    try {
      const text = await response.clone().text();
      if (text) message = text.slice(0, 300);
    } catch (_) {}
  }
  return attach(new Error(message), code, response.status);
}

/**
 * Builds an Error from a supabase.functions.invoke tuple. Handles both JS
 * client v2 behaviors: sometimes `data` is populated on non-2xx, sometimes
 * the body is only reachable via `error.context` (a Response).
 */
export async function errorFromAiInvoke({ data, error }, fallbackMessage = 'Request failed') {
  let code = null;
  let message = fallbackMessage;
  let status = typeof error?.context?.status === 'number' ? error.context.status : null;

  if (data?.error?.code) code = data.error.code;
  if (data?.error?.message) message = data.error.message;
  else if (typeof data?.text === 'string' && data.text) message = data.text;

  if (!code && error?.context && typeof error.context.clone === 'function') {
    try {
      const body = await error.context.clone().json();
      if (body?.error?.code) code = body.error.code;
      if (body?.error?.message) message = body.error.message;
      else if (typeof body?.text === 'string' && body.text) message = body.text;
      if (typeof error.context.status === 'number') status = error.context.status;
    } catch (_) {}
  }

  if (!code && !data && error?.message) {
    message = error.message;
  }

  return attach(new Error(message), code, status);
}
